/**
 * Mobile app only: remember the deep link a signed-out (or not-yet-confirmed) launch was
 * bounced from, so the couple lands back on it after sign-in — or after a slow profile load
 * finally restores the session — instead of on "/" (or on /login, which the signed-in client
 * route table does not have: it rendered NotFound).
 * sessionStorage: survives the in-app navigation, never outlives the app process.
 */
import { isClientSideRole } from "@/v2/lib/flag";

const KEY = "lm-app-return-to";

/** Only same-app paths are ever stored (never //host, never the login page itself). */
export function safeReturnPath(p: string | null | undefined): string | null {
  if (typeof p !== "string" || !p.startsWith("/") || p.startsWith("//")) return null;
  // "/\\host" is treated as "//host" by URL parsers — never a same-app path.
  if (p.includes("\\")) return null;
  const path = p.split("#")[0];
  if (path === "/" || path === "/login" || path.startsWith("/login?")) return null;
  return path;
}

export function rememberAppReturnTo(path: string): void {
  const safe = safeReturnPath(path);
  if (!safe) return;
  try { sessionStorage.setItem(KEY, safe); } catch { /* private mode */ }
}

/** One-shot. Falls back to "/". */
export function consumeAppReturnTo(): string {
  let v: string | null = null;
  try { v = sessionStorage.getItem(KEY); sessionStorage.removeItem(KEY); } catch { /* ignore */ }
  return safeReturnPath(v) ?? "/";
}

// ---------------------------------------------------------------------------
// WEB (audit 2026-09-27 L5). WhatsApp / email links into the client portal used to
// land a signed-out couple on the marketing Home, and login then went to "/".
// Same one-shot sessionStorage slot; honoured on the web ONLY for client-side roles and
// ONLY for these portal prefixes (whole path segment), so staff never land on a
// client page and nothing outside the portal is ever a post-login target.
// ---------------------------------------------------------------------------
export const PORTAL_RETURN_PREFIXES = [
  "/client/", "/bookings", "/invoices", "/event-form", "/event-day-form", "/mc-scheduling",
  "/website-v2", "/wedding-website-v2", "/family-hub", "/checklist", "/budget",
  "/client-transport", "/loyalty-program", "/story-weaver", "/visualizer", "/invitations",
  "/guests", "/timeline", "/dance-program-booking", "/files", "/support", "/notifications",
] as const;

export function isPortalReturnPath(p: string | null | undefined): boolean {
  const safe = safeReturnPath(p);
  if (!safe) return false;
  const path = safe.split("?")[0];
  return PORTAL_RETURN_PREFIXES.some((pre) =>
    pre.endsWith("/") ? path.startsWith(pre) && path.length > pre.length
      : path === pre || path.startsWith(pre + "/"));
}

/** Where to go after a successful sign-in. One-shot: always clears the stored path. */
export function postLoginPath(opts: { role?: string | null; isApp: boolean }): string {
  const stored = consumeAppReturnTo();
  if (opts.isApp) return stored; // mobile app: unchanged
  // Every client-side role (client, collaborator, planner — force-v2 2026-10-01).
  return isClientSideRole(opts.role) && isPortalReturnPath(stored) ? stored : "/";
}

/** Signed-out web catch-all: remember a portal deep link on the main host only. */
export function shouldRememberSignedOutPath(hostname: string, pathname: string): boolean {
  const h = (hostname || "").toLowerCase();
  const mainHost = h === "lamirage.co" || h === "www.lamirage.co" || h === "localhost" || h === "127.0.0.1";
  return mainHost && isPortalReturnPath(pathname);
}
